import React, {useState} from 'react';
import {Box} from '@mui/material';
import './FilterPanel.scss';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import ArrButton from '../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import {Add} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {resetProjectForm} from '../../../../redux/slice/projectFormSlice';
import {RoleNames} from '../../../../shared/constants';
import {RootState} from '../../../../redux/store';
import {setDomainData} from '../../../../redux/slice/domainDataSlice';

export type FilterPanelProps = {
  initialName?: string;
  initialClientName?: string;
  initialAlphaCode?: string;

  onApplyFilters: (name: string, clientName: string, alphaCode: string) => void;
  onKeyDownEvent?: (
    e: React.KeyboardEvent,
    name: string,
    clientName: string,
    alphaCode: string
  ) => void;
};

const FilterPanel: React.FC<FilterPanelProps> = ({
  initialName = '',
  initialClientName = '',
  initialAlphaCode = '',
  onApplyFilters,
  onKeyDownEvent,
}) => {
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  const userRole = loginUserData?.roleName;
  const [name, setName] = useState(initialName);
  const [clientName, setClientName] = useState(initialClientName);
  const [alphaCode, setAlphaCode] = useState(initialAlphaCode);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleNavigation = () => {
    dispatch(resetProjectForm());
    dispatch(setDomainData([]));
    navigate('/Project/CreateProject');
  };

  const appliedClientName = () =>
    userRole === RoleNames.SystemAdministrator ? clientName : '';

  const handleApply = () => {
    onApplyFilters(name, appliedClientName(), alphaCode);
  };

  // since state update is synchronus pass the updated filter values.
  const handleClear = (
    currName: string,
    currClientName: string,
    currAlphaCode: string
  ) => {
    onApplyFilters(currName, currClientName, currAlphaCode);
  };

  return (
    <Box className="view-project-filter-panel">
      <Box className="view-project-filter-panel-controls">
        <div
          className="view-project-item-flex"
          data-testid={`filter-panel-manage-projects-name-wrapper`}
        >
          <ArrTextField
            state="enabled"
            sizeVariant="medium"
            slotProps={{inputLabel: {shrink: false}}}
            placeholder={t('projectManagement.manageProjects.name')}
            aria-label={t('projectManagement.manageProjects.name')}
            dataTestId="filter-panel-project-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) =>
              onKeyDownEvent && onKeyDownEvent(e, name, clientName, alphaCode)
            }
            type="clear"
            onClear={() => {
              setName('');
              handleClear('', appliedClientName(), alphaCode);
            }}
          />
        </div>
        {userRole === RoleNames.SystemAdministrator && (
          <div
            className="view-project-item-flex"
            data-testid={`filter-panel-client-name-wrapper`}
          >
            <ArrTextField
              state="enabled"
              sizeVariant="medium"
              slotProps={{inputLabel: {shrink: false}}}
              placeholder={t('projectManagement.manageProjects.clientName')}
              aria-label={t('projectManagement.manageProjects.clientName')}
              dataTestId="filter-panel-client-name"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              onKeyDown={(e) =>
                onKeyDownEvent && onKeyDownEvent(e, name, clientName, alphaCode)
              }
              type="clear"
              onClear={() => {
                setClientName('');
                handleClear(name, '', alphaCode);
              }}
            />
          </div>
        )}
        <div
          className="view-project-item-flex"
          data-testid={`filter-panel-alpha-code-wrapper-${userRole}`}
        >
          <ArrTextField
            state="enabled"
            sizeVariant="medium"
            slotProps={{inputLabel: {shrink: false}}}
            placeholder={t('projectManagement.manageProjects.alphaCode')}
            aria-label={t('projectManagement.manageProjects.alphaCode')}
            dataTestId="filter-panel-alpha-code"
            value={alphaCode}
            onChange={(e) => setAlphaCode(e.target.value)}
            onKeyDown={(e) =>
              onKeyDownEvent && onKeyDownEvent(e, name, clientName, alphaCode)
            }
            type="clear"
            onClear={() => {
              setAlphaCode('');
              handleClear(name, appliedClientName(), '');
            }}
          />
        </div>
        <ArrButton
          aria-label={t('common.applyFilters')}
          variant="secondary"
          size="medium"
          onClick={handleApply}
          dataTestId="apply-filter-btn"
        >
          {t('common.applyFilters')}
        </ArrButton>
      </Box>
      <Box>
        <ArrButton
          variant="primary"
          size="medium"
          aria-label={t('projectManagement.manageProjects.createProject')}
          className="custom-button"
          startIcon={<Add className="add-icon" />}
          onClick={handleNavigation}
          dataTestId="filter-panel-create-project-button"
        >
          {t('projectManagement.manageProjects.createProject')}
        </ArrButton>
      </Box>
    </Box>
  );
};

export default FilterPanel;
